import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from './Navbar';
import VideoPlayer from './VideoPlayer';
import { Lock, Play, CheckCircle, ArrowRight, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, hasAccess, loading } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(null);
  const [loadingModules, setLoadingModules] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [modulesRes, progressRes] = await Promise.all([
          axios.get(`${API}/courses/modules`, { withCredentials: true }),
          axios.get(`${API}/user/progress`, { withCredentials: true })
        ]);
        setModules(modulesRes.data);
        setProgress(progressRes.data);
        
        // Set first unlocked module as selected
        const firstUnlocked = modulesRes.data.find(m => !m.is_locked);
        if (firstUnlocked) {
          setSelectedModule(firstUnlocked);
          setCurrentIndex(modulesRes.data.indexOf(firstUnlocked));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoadingModules(false);
      }
    };

    if (user) {
      fetchData();
    }
  }, [user]);

  const handleModuleChange = (module, index) => {
    setSelectedModule(module);
    setCurrentIndex(index);
  };

  const handleProgressUpdate = (moduleId, time, completed) => {
    // Update local state
    setModules(prev => prev.map(m => {
      if (m.module_id === moduleId) {
        return {
          ...m,
          progress: { ...m.progress, watched_seconds: time, is_completed: completed }
        };
      }
      // Unlock next module if current is completed
      if (completed) {
        const currentIdx = prev.findIndex(mod => mod.module_id === moduleId);
        if (prev.indexOf(m) === currentIdx + 1) {
          return { ...m, is_locked: false };
        }
      }
      return m;
    }));
  };

  if (loading || loadingModules) {
    return (
      <div className="min-h-screen bg-[#1a1f2e] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#d4a33a] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1f2e]">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4 max-w-7xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">
            Welcome back, <span className="text-[#d4a33a]">{user?.name?.split(' ')[0]}</span>
          </h1>
          {progress && (
            <p className="text-gray-400 mt-2">
              Course Progress: {progress.completed_modules}/{progress.total_modules} modules completed ({progress.completion_percentage}%)
            </p>
          )}
        </div>

        {/* Access Warning */}
        {!hasAccess && (
          <div className="mb-8 bg-amber-500/10 border border-amber-500 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <div>
                <h3 className="text-white font-semibold mb-2">Limited Access</h3>
                <p className="text-gray-300 mb-4">
                  You're viewing the free preview. Purchase the full course to unlock all 20 modules
                  and transform your life through sacred energy work.
                </p>
                <button 
                  onClick={() => navigate('/#enroll')}
                  className="bg-[#d4a33a] hover:bg-[#c49530] text-white px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors"
                >
                  Unlock Full Course
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {progress && (
          <div className="mb-8 bg-[#232836] rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-medium">Overall Progress</span>
              <span className="text-[#d4a33a] font-bold">{progress.completion_percentage}%</span>
            </div>
            <div className="h-3 bg-[#1a1f2e] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#d4a33a] to-[#f0c674] rounded-full transition-all duration-500"
                style={{ width: `${progress.completion_percentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Video Player */}
        {selectedModule ? (
          <VideoPlayer
            module={selectedModule}
            modules={modules}
            currentIndex={currentIndex}
            onModuleChange={handleModuleChange}
            onProgressUpdate={handleProgressUpdate}
          />
        ) : (
          <div className="bg-[#232836] rounded-xl p-12 text-center">
            <Lock className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">No Modules Available</h2>
            <p className="text-gray-400">Please purchase the course to access the content.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
